import React, { useState } from 'react';
import { useSelector } from 'react-redux';

const CalendarBlock = ({ data, isSelected, onSelect, onUpdate, isPreview }) => {
  const { themeStyles } = React.useContext(React.createContext({}));
  const { products } = useSelector(state => state.products || {});
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data);

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get appointment services
  const appointmentServices = products?.filter(product => 
    product.productType === 'appointment' || product.category === 'appointment'
  ) || [];

  if (isPreview) {
    return (
      <section style={{
        padding: '4rem 0',
        backgroundColor: themeStyles?.background || '#ffffff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: themeStyles?.text || '#333'
            }}>
              {data.title || 'Book an Appointment'}
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: themeStyles?.textLight || '#666',
              lineHeight: '1.6'
            }}>
              {data.description || 'Schedule a consultation with our experts'}
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '3rem'
          }}>
            {/* Calendar Preview */}
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: themeStyles?.text || '#333'
              }}>
                Select Date
              </h3>
              
              {/* Mock Calendar */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} style={{
                    textAlign: 'center',
                    padding: '0.5rem',
                    fontWeight: '600',
                    color: themeStyles?.textLight || '#666',
                    fontSize: '0.875rem'
                  }}>
                    {day}
                  </div>
                ))}
                {Array.from({ length: 35 }, (_, i) => {
                  const day = i - 6; // Start from day 1
                  const isCurrentMonth = day > 0 && day <= 31;
                  return (
                    <button
                      key={i}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        backgroundColor: isCurrentMonth ? 'white' : 'transparent',
                        color: isCurrentMonth ? themeStyles?.text || '#333' : 'transparent',
                        cursor: isCurrentMonth ? 'pointer' : 'default',
                        borderRadius: '0.25rem',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s ease'
                      }}
                      disabled={!isCurrentMonth}
                    >
                      {isCurrentMonth ? day : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Services Preview */}
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: themeStyles?.text || '#333'
              }}>
                Available Services
              </h3>
              
              {appointmentServices.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {appointmentServices.slice(0, 3).map((service) => (
                    <div
                      key={service._id}
                      style={{
                        padding: '1rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = themeStyles?.primary || '#007bff';
                        e.target.style.backgroundColor = '#f8f9ff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.backgroundColor = 'white';
                      }}
                    >
                      <h4 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        color: themeStyles?.text || '#333'
                      }}>
                        {service.name}
                      </h4>
                      <p style={{
                        color: themeStyles?.textLight || '#666',
                        fontSize: '0.875rem',
                        marginBottom: '0.5rem'
                      }}>
                        {service.description?.substring(0, 100)}...
                      </p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: themeStyles?.primary || '#007bff'
                        }}>
                          ${service.price}
                        </span>
                        <span style={{
                          fontSize: '0.875rem',
                          color: themeStyles?.textLight || '#666'
                        }}>
                          {service.appointmentSettings?.duration || 60} min
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: themeStyles?.textLight || '#666'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📅</div>
                  <p>No appointment services available</p>
                </div>
              )}
            </div>
          </div>

          {/* Booking Form Preview */}
          <div style={{
            marginTop: '3rem',
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1rem',
              color: themeStyles?.text || '#333'
            }}>
              Your Details
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: themeStyles?.text || '#333'
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: themeStyles?.text || '#333'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: themeStyles?.text || '#333'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  placeholder="Enter your phone"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <button
              style={{
                marginTop: '1.5rem',
                width: '100%',
                backgroundColor: themeStyles?.primary || '#007bff',
                color: 'white',
                padding: '1rem',
                border: 'none',
                borderRadius: '0.25rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = themeStyles?.secondary || '#0056b3';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = themeStyles?.primary || '#007bff';
              }}
            >
              Book Appointment
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div
      style={{
        border: isSelected ? '2px solid #007bff' : '2px solid #dee2e6',
        borderRadius: '0.5rem',
        backgroundColor: 'white',
        marginBottom: '1rem',
        position: 'relative'
      }}
      onClick={onSelect}
    >
      {/* Block Header */}
      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📅</span>
          <span style={{ fontWeight: '600' }}>Calendar Block</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(!isEditing);
          }}
          style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: isEditing ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Block Content */}
      <div style={{ padding: '1rem' }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Section Title
              </label>
              <input
                type="text"
                value={editData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="2"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Service Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Default Service
              </label>
              <select
                value={editData.serviceId || ''}
                onChange={(e) => handleInputChange('serviceId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select a service (optional)</option>
                {appointmentServices.map(service => (
                  <option key={service._id} value={service._id}>
                    {service.name} - ${service.price}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Options */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Display Options
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editData.showTimeSlots || false}
                    onChange={(e) => handleInputChange('showTimeSlots', e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  Show Time Slots
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editData.allowRescheduling || false}
                    onChange={(e) => handleInputChange('allowRescheduling', e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  Allow Rescheduling
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.25rem',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
              {data.title || 'Calendar Block'}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
              Appointment booking calendar
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarBlock;
